import { envFlags } from '@thoughtspot/env-flags';
import {
    FEATURE_FLAG_NAMES,
    isFeatureEnabled,
} from '@thoughtspot/feature-service';
import omitDeep from 'omit-deep-lodash';
import {
    AnswerColumn,
    AnswerColumnEffectiveType,
    ColumnAggregationType,
    FalconDataType,
    FilterTypes,
    PhraseType,
    RecognizedToken,
    RecognizedTokenType,
    SagePhrase,
} from '/@services/generated/graphql-types';

export enum EditModalType {
    ATTRIBUTE = 'ATTRIBUTE',
    BY_ATTRIBUTE = 'BY_ATTRIBUTE',
    ATTRIBUTE_FILTER = 'ATTRIBUTE_FILTER',
    AGGREGATED_ATTRIBUTE = 'AGGREGATED_ATTRIBUTE',
    ATTRIBUTE_VALUE_ONLY_FILTER = 'ATTRIBUTE_VALUE_ONLY_FILTER',
    MEASURE = 'MEASURE',
    MEASURE_FILTER = 'MEASURE_FILTER',
    AGGREGATED_MEASURE = 'AGGREGATED_MEASURE',
    DATE_COLUMN = 'DATE_COLUMN',
    DATE_FILTER = 'DATE_FILTER', // <date> <> <> <> type filter
    DATE_VALUE_ONLY_FILTER = 'DATE_VALUE_ONLY_FILTER', // date column value only filter
    NONE = 'NONE',
}

export const isFromFormula = (phrase: SagePhrase): boolean => {
    return phrase.tokens.some(token => token?.isFromFormula);
};

export const checkIfPhraseIsAttributeFilter = (phrase: SagePhrase): boolean => {
    return (
        phrase.tokens.length >= 3 &&
        phrase.tokens[0].type === RecognizedTokenType.Attribute &&
        ![
            FalconDataType.Date,
            FalconDataType.DateTime,
            FalconDataType.Time,
        ].includes(phrase.tokens[0].dataType) &&
        phrase.tokens[1].type === RecognizedTokenType.Operator &&
        phrase.tokens[2].type === RecognizedTokenType.Value &&
        phrase.type === PhraseType.FilterPhrase &&
        !isFromFormula(phrase)
    );
};

export const checkIfAttributeValueOnlyFilterPhrase = (
    phrase: SagePhrase,
): boolean => {
    const isFilterPhrase =
        phrase.type === PhraseType.FilterPhrase ||
        phrase.type === PhraseType.FilteredCountPhrase ||
        phrase.type === PhraseType.GeofilterPhrase ||
        phrase.type === PhraseType.InFilterPhrase ||
        phrase.type === PhraseType.OfFilterPhrase ||
        phrase.type === PhraseType.InFilterSubqueryPhrase ||
        phrase.type === PhraseType.PercentageOfFilterPhrase;
    return (
        phrase.tokens.length >= 1 &&
        isFilterPhrase &&
        phrase.tokens.every(
            token =>
                token.type === RecognizedTokenType.Value &&
                token.dataType === FalconDataType.Char &&
                token?.column?.columnGuid ===
                    phrase.tokens[0]?.column?.columnGuid,
        ) &&
        phrase.type === PhraseType.FilterPhrase &&
        !isFromFormula(phrase)
    );
};

export const checkIfPhraseIsDateColumn = (phrase: SagePhrase): boolean => {
    return (
        phrase.tokens.length === 1 &&
        phrase.type === PhraseType.GroupByColumnPhrase &&
        phrase.tokens[0].type === RecognizedTokenType.Attribute &&
        phrase.tokens[0].dataType === FalconDataType.Date &&
        !isFromFormula(phrase)
    );
};

export const checkIfPhraseIsDateFilter = (phrase: SagePhrase): boolean => {
    return (
        phrase.tokens.length >= 3 && // <date> <op> <value> extend for other types
        phrase.tokens[0].type === RecognizedTokenType.Attribute &&
        [
            FalconDataType.Date,
            FalconDataType.DateTime,
            FalconDataType.Time,
        ].includes(phrase.tokens[0].dataType) &&
        phrase.type === PhraseType.FilterPhrase &&
        !isFromFormula(phrase)
    );
};

export const checkIfPhraseIsDateValueOnlyFilter = (
    phrase: SagePhrase,
): boolean => {
    return (
        phrase.tokens.length === 1 &&
        phrase.type === PhraseType.FilterPhrase &&
        phrase.tokens[0].type === RecognizedTokenType.Value &&
        [
            FalconDataType.Date,
            FalconDataType.DateTime,
            FalconDataType.Time,
        ].includes(phrase.tokens[0].dataType) &&
        phrase.type === PhraseType.FilterPhrase &&
        !isFromFormula(phrase)
    );
};

export const checkIfAttributeColumn = (phrase: SagePhrase): boolean => {
    return (
        phrase.type === PhraseType.GroupByColumnPhrase &&
        phrase.tokens.length === 1 &&
        phrase.tokens[0].type === RecognizedTokenType.Attribute &&
        !isFromFormula(phrase)
    );
};

export const checkIfMeasureColumn = (phrase: SagePhrase): boolean => {
    return (
        phrase.type === PhraseType.AggregatedColumnPhrase &&
        phrase.tokens.length === 1 &&
        phrase.tokens[0].type === RecognizedTokenType.Measure &&
        !isFromFormula(phrase)
    );
};

export const checkIfByAttributeColumn = (phrase: SagePhrase): boolean => {
    return (
        phrase.type === PhraseType.GroupByColumnPhrase &&
        phrase.tokens.length === 2 &&
        phrase.tokens[0].type === RecognizedTokenType.Keyword &&
        phrase.tokens[1].type === RecognizedTokenType.Attribute &&
        !isFromFormula(phrase)
    );
};

export const checkIfPhraseIsMeasureFilter = (phrase: SagePhrase): boolean => {
    return (
        phrase.tokens.length === 3 &&
        phrase.tokens[0].type === RecognizedTokenType.Measure &&
        phrase.tokens[1].type === RecognizedTokenType.Operator &&
        phrase.tokens[2].type === RecognizedTokenType.Value &&
        phrase.type === PhraseType.FilterPhrase &&
        !isFromFormula(phrase)
    );
};

export const checkIfPhraseIsAggregatedMeasure = (
    phrase: SagePhrase,
): boolean => {
    return (
        phrase.type === PhraseType.AggregatedColumnPhrase &&
        phrase.tokens.length >= 2 &&
        phrase.tokens[0].type === RecognizedTokenType.Keyword &&
        phrase.tokens[1].type === RecognizedTokenType.Measure &&
        !isFromFormula(phrase)
    );
};

export const checkIfPhraseIsAggregatedAttribute = (
    phrase: SagePhrase,
): boolean => {
    return (
        phrase.type === PhraseType.AggregatedColumnPhrase &&
        phrase.tokens.length >= 2 &&
        phrase.tokens[0].type === RecognizedTokenType.Keyword &&
        phrase.tokens[1].type === RecognizedTokenType.Attribute &&
        !isFromFormula(phrase)
    );
};

export const getEditModalTypeFromPhrase = (
    phrase: SagePhrase,
): EditModalType => {
    if (checkIfPhraseIsDateColumn(phrase)) {
        return EditModalType.DATE_COLUMN;
    }
    if (checkIfAttributeValueOnlyFilterPhrase(phrase)) {
        return EditModalType.ATTRIBUTE_VALUE_ONLY_FILTER;
    }
    if (checkIfPhraseIsAttributeFilter(phrase)) {
        return EditModalType.ATTRIBUTE_FILTER;
    }
    if (checkIfPhraseIsAggregatedMeasure(phrase)) {
        return EditModalType.AGGREGATED_MEASURE;
    }
    if (checkIfPhraseIsAggregatedAttribute(phrase)) {
        return EditModalType.AGGREGATED_ATTRIBUTE;
    }
    if (checkIfPhraseIsMeasureFilter(phrase)) {
        return EditModalType.MEASURE_FILTER;
    }
    if (checkIfPhraseIsDateFilter(phrase)) {
        return EditModalType.DATE_FILTER;
    }
    if (checkIfAttributeColumn(phrase)) {
        return EditModalType.ATTRIBUTE;
    }
    if (checkIfByAttributeColumn(phrase)) {
        return EditModalType.BY_ATTRIBUTE;
    }
    if (checkIfMeasureColumn(phrase)) {
        return EditModalType.MEASURE;
    }
    if (checkIfPhraseIsDateValueOnlyFilter(phrase)) {
        return EditModalType.DATE_VALUE_ONLY_FILTER;
    }
    return EditModalType.NONE;
};

export const canEditTokens = (
    phrase: SagePhrase,
    showEditSageTokens = true,
) => {
    if (!showEditSageTokens) {
        return false;
    }
    const atacEnabled = isFeatureEnabled(
        FEATURE_FLAG_NAMES.ENABLE_SPOTTER_ATAC_WORKFLOW,
        {
            urlParamValue: envFlags.getValue(
                FEATURE_FLAG_NAMES.ENABLE_SPOTTER_ATAC_WORKFLOW,
            ),
        },
    ) as boolean;
    const editModalType = getEditModalTypeFromPhrase(phrase);
    return atacEnabled && editModalType !== EditModalType.NONE;
};

export const mapOperatorToFilterTypes = (operator: string): FilterTypes => {
    if (!operator) {
        return null;
    }
    switch (operator.toLowerCase()) {
        case '=':
            return FilterTypes.Eq;
        case '!=':
            return FilterTypes.Ne;
        case '>':
            return FilterTypes.Gt;
        case '>=':
            return FilterTypes.Ge;
        case '<':
            return FilterTypes.Lt;
        case '<=':
            return FilterTypes.Le;
        // check if locale has any effect on text operator values
        case 'contains':
            return FilterTypes.Contains;
        case 'begins with':
            return FilterTypes.BeginsWith;
        case 'ends with':
            return FilterTypes.EndsWith;
        case 'in':
            return FilterTypes.In;
        default:
            return null;
    }
};

export const mapAggregationToColumnAggregationType = (
    aggregation = '',
): ColumnAggregationType => {
    if (!aggregation) {
        return ColumnAggregationType.None;
    }
    switch (aggregation.toLowerCase()) {
        case 'sum':
            return ColumnAggregationType.Sum;
        case 'average':
            return ColumnAggregationType.Average;
        case 'count':
            return ColumnAggregationType.Count;
        case 'max':
            return ColumnAggregationType.Max;
        case 'min':
            return ColumnAggregationType.Min;
        case 'unique count':
            return ColumnAggregationType.CountDistinct;
        case 'standard deviation':
            return ColumnAggregationType.StdDeviation;
        default:
            return ColumnAggregationType.None;
    }
};

export const mapColumnAggregationTypeToAggregation = (
    aggregationType: ColumnAggregationType,
): string | null => {
    switch (aggregationType) {
        case ColumnAggregationType.Sum:
            return 'sum';
        case ColumnAggregationType.Average:
            return 'average';
        case ColumnAggregationType.Count:
            return 'count';
        case ColumnAggregationType.Max:
            return 'max';
        case ColumnAggregationType.Min:
            return 'min';
        case ColumnAggregationType.CountDistinct:
            return 'unique count';
        case ColumnAggregationType.StdDeviation:
            return 'standard deviation';
        default:
            return null;
    }
};

export const getAllowedPhraseType = (
    columnType: RecognizedTokenType.Attribute | RecognizedTokenType.Measure,
) => {
    return columnType === RecognizedTokenType.Attribute
        ? PhraseType.GroupByColumnPhrase
        : PhraseType.AggregatedColumnPhrase;
};

export const getTokensForEditModal = (
    tokens: RecognizedToken[],
    editModalType: EditModalType,
    allColumns: AnswerColumn[],
) => {
    if (editModalType === EditModalType.BY_ATTRIBUTE) {
        return tokens.slice(1);
    }
    if (editModalType === EditModalType.MEASURE) {
        const targetGuid = tokens[0].guid;
        const answerColumn = allColumns.filter(
            column =>
                column.referencedColumns.some(
                    refColumn => refColumn.guid === targetGuid,
                ) &&
                column.type === AnswerColumnEffectiveType.Measure &&
                column.aggregationType,
        );
        let aggregationToken: RecognizedToken = {
            token: '',
            typeEnum: RecognizedTokenType.Keyword,
        };
        if (answerColumn.length > 0) {
            const aggregationType = answerColumn[0].aggregationType;
            aggregationToken = {
                token: mapColumnAggregationTypeToAggregation(aggregationType),
                typeEnum: RecognizedTokenType.Keyword,
            };
        }
        return [aggregationToken, ...tokens];
    }
    if (editModalType === EditModalType.ATTRIBUTE_VALUE_ONLY_FILTER) {
        const attributeColumn: RecognizedToken = {
            canonicalForm: tokens[0].tokenMetadata.name,
            dataType: tokens[0].dataType,
            guid: tokens[0].guid,
            token: tokens[0].tokenMetadata.name,
            typeEnum: RecognizedTokenType.Attribute,
            tokenMetadata: tokens[0].tokenMetadata,
        };
        const operatorToken: RecognizedToken = {
            token: '=',
            typeEnum: RecognizedTokenType.Operator,
        };
        return [attributeColumn, operatorToken, ...tokens];
    }
    if (editModalType === EditModalType.DATE_VALUE_ONLY_FILTER) {
        const dateColumn: RecognizedToken = {
            canonicalForm: tokens[0].tokenMetadata.name,
            dataType: tokens[0].dataType,
            guid: tokens[0].guid,
            token: tokens[0].tokenMetadata.name,
            typeEnum: RecognizedTokenType.Attribute,
            tokenMetadata: tokens[0].tokenMetadata,
        };
        const operatorToken: RecognizedToken = {
            token: '=',
            typeEnum: RecognizedTokenType.Operator,
        };
        return [dateColumn, operatorToken, ...tokens];
    }
    return tokens;
};

export const getCompletionFromRecognizedToken = (
    recTokenIp: RecognizedToken,
) => {
    return omitDeep(recTokenIp, [
        '__typename',
        'outputGuid',
        'joinPath',
        'whiteSpaceSuffix',
        'isFormula',
        'isCohort',
    ]);
};
